import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  Observable,
  of,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  finalize,
  retry,
  shareReplay,
  switchMap,
} from 'rxjs/operators';

import { ApiException } from '../api/generated/auth.client';
import { AuthStore } from '../state/auth.store';

let refreshInProgress = false;
let refresh$: Observable<string | null> | null = null;
let logoutInProgress = false;
export const SKIP_AUTH_REFRESH = new HttpContextToken<boolean>(() => false);
export const REFRESH_RETRY = new HttpContextToken<boolean>(() => false);
const safeLogout = (authStore: AuthStore) => {
  if (logoutInProgress) return;
  logoutInProgress = true;
  authStore.sessionExpiredLogout();
};

export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);

  if (req.context.get(SKIP_AUTH_REFRESH)) return next(req);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) return throwError(() => error);

      const path = (() => {
        try { return new URL(req.url).pathname; } catch { return req.url; }
      })();

      const isAuthEndpoint =
        path.includes('/hub/auth/login') ||
        path.includes('/hub/auth/register') ||
        path.includes('/hub/auth/refresh') ||
        path.includes('/hub/auth/logout');

      if (error.status !== 401 || isAuthEndpoint) {
        return throwError(() => error);
      }

      if (req.context.get(REFRESH_RETRY)) {
        safeLogout(authStore);
        return throwError(() => error);
      }


      if (!refreshInProgress) {
        refreshInProgress = true;

        refresh$ = authStore.refreshAccessToken$().pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => {
              const status =
                error instanceof HttpErrorResponse ? error.status
                : ApiException.isApiException(error) ? error.status
                : undefined;

              if (status === 0 || status === 504) {
                return timer(1000 * retryCount);
              }
              throw error;
            },
          }),
          catchError(() => of(null)),
          finalize(() => {
            refreshInProgress = false;
            refresh$ = null;
          }),
          shareReplay(1),
        );
      }

      return (refresh$ ?? of(null)).pipe(
        switchMap((newAccessToken) => {
          if (!newAccessToken) {
            safeLogout(authStore);
            return throwError(() => error);
          }

          const retryReq = req.clone({
            context: req.context.set(REFRESH_RETRY, true),
            setHeaders: { Authorization: `Bearer ${newAccessToken}` },
          });

          return next(retryReq);
        }),
      );
    }),
  );
};
